import { useState } from 'react';
import { X, UserRound, Activity } from 'lucide-react';
import api from '@/api';
import ModalShell from '@/components/shared/ModalShell';
import { toast } from 'sonner';

/**
 * Correction manuelle du statut clinique (IDE / médecin).
 * Règle : effective immédiatement, valable jusqu'au prochain check-in ou nouvelle correction. Ne touche pas aux alertes.
 */

const STATUS_OPTIONS = [
  { value: "STABLE", label: "Stable", desc: "Aucun signe préoccupant", active: "border-emerald-500 bg-emerald-50 text-emerald-800" },
  { value: "TO_MONITOR", label: "À surveiller", desc: "Surveillance renforcée", active: "border-amber-500 bg-amber-50 text-amber-800" },
  { value: "CRITICAL", label: "Critique", desc: "Prise en charge urgente", active: "border-rose-500 bg-rose-50 text-rose-800" },
];

export function formatStatusSource(meta) {
  if (!meta) return null;
  const when = meta.at ? new Date(meta.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "";
  if (meta.source === "manual") return `Corrigé par ${meta.actorName || "un soignant"} le ${when}${meta.reason ? ` — ${meta.reason}` : ""}`;
  return `Calculé depuis le check-in du ${when}`;
}

export default function StatusChangeModal({
  isOpen,
  onClose,
  patientId,
  currentStatus,
  statusMeta,
  newStatus,
  setNewStatus,
  transmission,
  setTransmission,
  onConfirm,
  onUpdated,
  loading,
}) {
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const effectiveLoading = loading || submitting;
  const handleConfirm = async () => {
    if (onConfirm) {
      onConfirm();
      return;
    }
    if (!patientId) return;
    setSubmitting(true);
    try {
      await api.patch(`/patients/${patientId}/status`, {
        status: newStatus,
        transmission,
      });
      toast.success("Statut clinique corrigé");
      onClose();
      setTransmission("");
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const sourceLine = formatStatusSource(statusMeta);

  return (
    <ModalShell testId="status-change-modal">
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">Corriger le statut clinique</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg" data-testid="status-modal-close">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          {sourceLine && (
            <p className="mt-2 text-xs text-slate-600 leading-snug" data-testid="status-modal-source">
              {statusMeta?.source === "manual" ? <UserRound className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> : <Activity className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
              Statut actuel : <strong>{currentStatus}</strong> · {sourceLine}
            </p>
          )}
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nouveau statut clinique</label>
            <div className="grid grid-cols-3 gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setNewStatus(opt.value)}
                  data-testid={`status-option-${opt.value}`}
                  className={`p-3 rounded-xl border-2 text-left transition-colors ${
                    newStatus === opt.value ? opt.active : "border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <span className="block text-sm font-semibold">{opt.label}</span>
                  <span className="block text-[11px] opacity-80">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="status-modal-rule">
            Cette correction prend effet immédiatement et sera remplacée par le prochain check-in du patient.
            Elle ne clôture aucune alerte : les alertes se traitent séparément.
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Justification (transmission) *
            </label>
            <textarea
              value={transmission}
              onChange={e => setTransmission(e.target.value)}
              placeholder="Ex. : questionnaire mal renseigné, patient joint par téléphone, état clinique rassurant… (minimum 10 caractères)"
              rows={4}
              data-testid="status-modal-reason"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none"
            />
            <p className="text-xs text-slate-400 mt-1">{transmission.length}/10 caractères minimum</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={effectiveLoading || transmission.length < 10 || !newStatus || newStatus === currentStatus}
              data-testid="status-modal-confirm"
              className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {effectiveLoading ? "..." : "Confirmer la correction"}
            </button>
          </div>
        </div>
    </ModalShell>
  );
}
